/** biome-ignore-all lint/suspicious/noArrayIndexKey: biome ignore */
'use client'

import cn from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import { type FC, useCallback, useEffect, useState } from 'react'
import type { Content } from '@/content-types'
import type { SliceComponentProps } from '@/lib/rich-text'
import { ContentNextImage } from '@/lib/rich-text'

/**
 * Props for `ReviewCard`.
 */
export type ReviewCardProps = SliceComponentProps<Content.ReviewCardSlice>

/**
 * Component for "ReviewCard" Slices.
 */
const ReviewCard: FC<ReviewCardProps> = ({ slice }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: 3,
    align: 'start',
  })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    emblaApi.on('select', onSelect)
    onSelect()

    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  const totalCards = slice.primary.reviewcards.length
  const totalDots = totalCards

  // Calculate which dot should be active based on the current scroll position
  // Since we scroll by 3, each scroll position shows cards starting at index * 3
  const activeDotIndex = Math.min(selectedIndex * 3, totalCards - 1)

  const handleDotClick = (cardIndex: number) => {
    if (!emblaApi) return
    // Scroll to the group that contains this card
    const groupIndex = Math.floor(cardIndex / 3)
    scrollTo(groupIndex)
  }

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={cn('container flex flex-col items-center gap-8 py-10 md:gap-2.5 md:space-y-0 md:py-16')}
    >
      <div className="w-full overflow-hidden" ref={emblaRef}>
        <div className="flex gap-5">
          {slice.primary.reviewcards.map((reviewCard) => (
            <div
              key={reviewCard.name}
              className="flex min-w-0 flex-[0_0_33.333%] flex-col rounded-[20px] bg-[#F9F7F9] p-11"
            >
              <p className="text-[23px] leading-8 font-light text-[#282828E5] italic">{reviewCard.text}</p>
              <div className="mt-3 flex flex-row items-center gap-x-4">
                <ContentNextImage field={reviewCard.image} width={45} height={45} />
                <div className="flex flex-col">
                  <h5 className="text-foreground font-rubik text-lg font-normal tracking-tight">
                    {reviewCard.name}
                  </h5>
                  <p className="text-primary text-[15px] font-normal tracking-tight uppercase">
                    {reviewCard.reviewertitle}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {totalDots > 3 && (
        <div className="mt-4 ml-4 flex w-full justify-start gap-2">
          {Array.from({ length: totalDots }).map((_, index) => {
            // Show 3 dots centered around the first visible card when possible
            let startDot = activeDotIndex - 1
            let endDot = activeDotIndex + 1

            // If we can't go before 0, show first 3 dots
            if (startDot < 0) {
              startDot = 0
              endDot = 2
            }
            // If we can't go after the last dot, show last 3 dots
            if (endDot >= totalDots) {
              endDot = totalDots - 1
              startDot = Math.max(0, endDot - 2)
            }

            const isInActiveGroup = index >= startDot && index <= endDot
            return (
              <button
                type="button"
                key={`review-card-dot-${index}`}
                onClick={() => handleDotClick(index)}
                className={cn(
                  'h-2 w-2 rounded-full text-[#C147E9] transition-opacity',
                  isInActiveGroup ? 'text-[#C147E9]' : 'text-[#C147E980]'
                )}
                style={{
                  backgroundColor: 'currentColor',
                }}
                aria-label={`Go to review card ${index + 1}`}
              />
            )
          })}
        </div>
      )}
    </section>
  )
}

export default ReviewCard
